import React from 'react'
import { useStyletron } from 'styletron-react'

// The promo page is framed at 1280px in Figma with 86px side margins, so the
// designed content column is 1280 − 2 × 86 = 1108px. Every band paints its
// background full-bleed but caps its contents at this width, which keeps the
// layout identical to Figma at 1280px and stops it stretching past that.
export const CONTENT_MAX_WIDTH = '1108px'

type Props = {
  children: React.ReactNode
  /**
   * Full CSS `background` shorthand — e.g. a `linear-gradient(...)`. Wins over
   * `backgroundColor` when set, because a gradient cannot ride `background-color`.
   * The activation hero (ConfiguredSummary, AI-891) uses this for its mint wash.
   */
  background?: string
  /** Band background. Defaults to transparent (the page's white base shows). */
  backgroundColor?: string
  /** The band's own padding shorthand, e.g. `'48px 86px'`. */
  padding?: string
  /**
   * Override the default content cap. HelpSection's Figma frame uses a 1080px
   * column on a 48px gutter rather than the 1108/86 of the other bands.
   */
  contentMaxWidth?: string
}

/**
 * Full-bleed section band with a centered, width-capped content column.
 *
 * The horizontal padding stays on the outer element so the gutters survive at
 * narrow widths; the cap lives on the inner element, which is what centers once
 * the viewport is wider than the designed 1280px frame.
 */
const SectionShell = ({
  background,
  backgroundColor = 'transparent',
  children,
  contentMaxWidth = CONTENT_MAX_WIDTH,
  padding = '0',
}: Props) => {
  const [css] = useStyletron()

  return (
    <section
      className={css({
        ...(background ? { background } : { backgroundColor }),
        padding,
      })}
    >
      <div
        className={css({
          margin: '0 auto',
          maxWidth: contentMaxWidth,
          width: '100%',
        })}
      >
        {children}
      </div>
    </section>
  )
}

export default SectionShell
